import type { User } from "@/components/Editor/types";
import { Loading } from "@/components/Loading";
import type { FileSystem } from "@/utils/yjs/FileSystem";
import {
	LiveMessageType,
	liveMessageSchema,
	stringToJSONSchema,
} from "@acme/shared-types";
import type { HocuspocusProvider } from "@hocuspocus/provider";
import { camelCase } from "lodash";
import React, { type ReactNode, useEffect } from "react";
import { useLocalStorage } from "usehooks-ts";
import type { Awareness } from "y-protocols/awareness";
import type * as Y from "yjs";
import { useEditorStateStore } from "./editorStateStore";
import { terminalStdout } from "./terminalObservables";

export interface YJSContextType {
	ydoc: Y.Doc;
	syncProvider?: HocuspocusProvider;
	awareness?: Awareness;
	yfs: FileSystem;
	awarenessUsers: Record<number, User>;
	shareCode?: string;
	setShareCode: (shareCode?: string) => void;
}

const EditorStateContext = React.createContext<YJSContextType | undefined>(
	undefined,
);

export interface YJSProviderProps {
	children: ReactNode;
}

export const EditorStateProvider = ({ children }: YJSProviderProps) => {
	const {
		ydoc,
		syncProvider,
		awareness,
		yfs,
		awarenessUsers,
		setShareCode,
		shareCode,
		projectId,
	} = useEditorStateStore();

	const [storedShareCodes, setStoredShareCodes] = useLocalStorage<
		Record<string, string>
	>("storedShareCodes", {});

	// Persist the share code to local
	useEffect(() => {
		if (shareCode && projectId) {
			setStoredShareCodes((prev) => ({
				...prev,
				[projectId]: shareCode,
			}));
		}
	}, [shareCode, projectId, setStoredShareCodes]);

	// Grab from local if possible
	useEffect(() => {
		if (projectId && !shareCode) {
			setShareCode(storedShareCodes[projectId]);
		}
	}, [projectId, shareCode, storedShareCodes, setShareCode]);

	// Project terminal stdout to the sync provider
	useEffect(() => {
		if (syncProvider) {
			// Send messages from the terminal to the sync provider
			const terminalStdoutSubscription = terminalStdout.subscribe({
				next: (v) => {
					// Only send messages that come from the local
					if (!v.remote) {
						syncProvider.sendStateless(
							JSON.stringify({
								type: LiveMessageType.TerminalStdout,
								content: v.content,
							}),
						);
					}
				},
			});

			// Persist incoming messages to the terminal
			const onStateless = (data) => {
				// Parse the message as zod schema
				const message = stringToJSONSchema
					.pipe(liveMessageSchema)
					.safeParse(data.payload);

				if (
					message.data &&
					message.data.type === LiveMessageType.TerminalStdout
				) {
					terminalStdout.next({
						// Came from a remote source
						remote: true,
						content: message.data.content,
					});
				}
			};
			syncProvider.on("stateless", onStateless);

			return () => {
				syncProvider.off("stateless", onStateless);
				terminalStdoutSubscription.unsubscribe();
			};
		}
	});

	if (!ydoc || !yfs) {
		return <Loading message="Loading Yjs" />;
	}

	return (
		<EditorStateContext.Provider
			value={{
				ydoc,
				syncProvider,
				awareness,
				yfs,
				awarenessUsers,
				setShareCode,
			}}
		>
			{children}
		</EditorStateContext.Provider>
	);
};

/**
 * @deprecated Use `useEditorStateStore` instead.
 */
export const useEditorState = () => {
	const context = React.useContext(EditorStateContext);
	if (context === undefined) {
		throw new Error("useEditorState must be used within a YJSProvider");
	}
	return context;
};
